/**
 * Error types for the device registration flow.
 * Each error type maps to specific user-facing messages and retry behaviors.
 *
 * Separated from the service layer so these can be used in pure contexts
 * (effects, type guards) without pulling in Angular dependency injection.
 */
export enum RegistrationError {
  /** Challenge token has expired (AC4) - User can restart the flow */
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',

  /** Device is already assigned to another account (AC5) - Auto-retries blocked, must unassign first */
  DEVICE_ALREADY_CLAIMED = 'DEVICE_ALREADY_CLAIMED',

  /** Invalid signature, certificate, or malformed request (AC6) - Retry allowed with logging */
  VALIDATION_FAILED = 'VALIDATION_FAILED',

  /** Rate limit exceeded (AC7) - Disabled until backoff expires */
  RATE_LIMITED = 'RATE_LIMITED',

  /** Network connectivity issue (AC8) - Retry allowed, state cleared on restart */
  NETWORK_ERROR = 'NETWORK_ERROR',

  /** Device not found (unassign) - Device does not exist or is not assigned to this account */
  DEVICE_NOT_FOUND = 'DEVICE_NOT_FOUND',
}

/**
 * Type guard: checks whether the given value is a valid RegistrationError enum member.
 * Used at effect boundaries to validate that the service threw a known error before
 * forwarding it downstream (otherwise NETWORK_ERROR is used as a safe fallback).
 */
export function isRegistrationError(error: unknown): error is RegistrationError {
  return typeof error === 'string' && Object.values(RegistrationError).includes(error as RegistrationError);
}
